import React, { useEffect, useState, useContext } from "react";

import { makeStyles, Typography } from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";
import CreateIcon from "@material-ui/icons/Create";
import moment from "moment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { HospitalsContext } from "../RodaHomepage";
import { getHospitalSurveyById, getOptionalQuestions } from "../../../api/hospital";
import { HospitalSurvey } from "../../../types/hospital";
import { blankSurvey } from "../../../constants/objects";
import { errorCodes } from "../../../constants/errors";
import SurveyTable from "../../../components/SurveyTable";
import NoData from "../../../components/NoData";
import Button from "../../../components/Button";
import ModalDialog from "../../../components/Form/ModalDialog";
import HospitalSurveyForm from "../../../components/Form/HospitalSurveyForm";
import { OptionalQuestion } from "../../../components/Form/types";
import NurseStatusBar from "../../../pages/NurseHomepage/components/NurseStatusBar";

const useStyles = makeStyles((theme: Theme) => ({
  responsiblePerson: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(0, 2),
    border: `1px solid ${theme.palette.secondary.dark}`,
    background: theme.palette.grey[100],
    borderRadius: 4,
    color: theme.palette.grey[600],
    fontSize: 14,
    display: "flex",
    alignItems: "center",
  },
  editIcon: {
    fontSize: theme.spacing(2.2),
    color: theme.palette.primary.main,
  },
  title: {
    color: theme.palette.primary.main,
    display: "inline-block",
    fontSize: theme.spacing(2.5),
    fontWeight: "bold",
    margin: theme.spacing(1.8),
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
}));

export default function RodaSurveyTable() {
  const classes = useStyles();
  const { currentHospital } = useContext(HospitalsContext);
  const [surveyData, setSurveyData] = useState<HospitalSurvey>(blankSurvey);
  const [optionalQuestions, setOptionalQuestions] = useState<OptionalQuestion[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    async function fetchData() {
      if (!currentHospital) {
        setSurveyData(blankSurvey);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        const optionalQuestionList = await getOptionalQuestions();
        setOptionalQuestions(optionalQuestionList.data);

        const response = await getHospitalSurveyById(currentHospital);
        setSurveyData(response.data);
      } catch (err: any) {
        if (err?.response?.status === errorCodes.NOT_FOUND) {
          setSurveyData(blankSurvey);
        } else {
          console.error("Error fetching survey:", err);
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [currentHospital]);

  const toggleOpen = () => {
    setModalOpen((modalOpen) => !modalOpen);
  };

  const isSurveyBlank = (surveyData: HospitalSurvey): boolean => {
    for (const category of surveyData.answers) {
      for (const surveyAnswer of category.surveyAnswers) {
        if (surveyAnswer.answerText && surveyAnswer.answerText.length > 0) {
          return false;
        }
      }
    }
    return true;
  };

  if (loading) {
    return <NoData>Učitavanje podataka...</NoData>;
  }

  if (error) {
    return <NoData>Nema podataka o anketi za odabranu ustanovu.</NoData>;
  }

  const handleFormDataChange = (survey: HospitalSurvey) => {
    setSurveyData(survey);
  };

  return (
    <>
      <div className={classes.container}>
        {!!surveyData.answers.length && !isSurveyBlank(surveyData) && (
          <Typography variant="body1" className={classes.responsiblePerson}>
            Zadnje ažurirano:{" "}
            {surveyData.userName + " " + surveyData.userSurname},{" "}
            {moment(surveyData.lastUpdate).format("DD.MM.YYYY. u HH:mm")}
          </Typography>
        )}
        {!!surveyData.answers.length && !isSurveyBlank(surveyData) && (
          <NurseStatusBar status={surveyData.status} icon={<CheckCircleIcon />} />
        )}
          <Button
            type="button"
            startIcon={<CreateIcon />}
            onClick={toggleOpen}
            style={{ marginLeft: "auto", marginTop: 0 }}
          >
            {!surveyData.answers.length ? "Unesi" : "Uredi"}
          </Button>
      </div>
      {!surveyData.answers.length && (
        <NoData>
          Nema podataka.
          <br />
          Kliknite na gumb "Unesi" kako biste unijeli podatke.
        </NoData>
      )}
      {!!surveyData.answers.length && !isSurveyBlank(surveyData) && (
        <SurveyTable surveyData={surveyData} optionalQuestions={optionalQuestions} />
      )}
      <ModalDialog
        modalOpen={modalOpen}
        handleClose={toggleOpen}
        maxWidth="lg"
        icon={<CreateIcon className={classes.editIcon} />}
        title={
          <Typography className={classes.title}>
            {!surveyData.answers.length ? "Unesi" : "Uredi"}
          </Typography>
        }
      >
        <HospitalSurveyForm
          handleClose={toggleOpen}
          surveyData={surveyData}
          handleDataChange={handleFormDataChange}
          hospitalId={currentHospital}
          optionalQuestions={optionalQuestions}
        />
      </ModalDialog>
    </>
  );
}
